// SPDX-License-Identifier: Apache-2.0

// useVault — Solid hook over @tether/storage's VaultStore.
//
// Returns reactive accessors driven by the store's subscribe() callback. The
// store itself stays UI-agnostic; this hook is the Solid translation of the
// React `use-vault.ts` hook from the old repo.
//
// Surface (close to the original, minus collab/Yjs presence which is a
// separate stream):
//
//   docs()         — sorted VaultDoc[] (newest first)
//   activeId()     — currently selected doc id, or null
//   activeDoc()    — VaultDoc | null
//   ready()        — true once init() has completed
//   ingesting()    — true while a drag-drop / picker batch is being parsed
//   toast()        — string | null, last short-lived status message
//
// Mutations are plain async functions; callers `await` them.

import { createSignal, createMemo, onCleanup, onMount } from "solid-js";
import {
  getVaultStore,
  getWorkspacesManager,
  parseFiles,
  isIngestable,
  fsAccessSupported,
  getVaultDir,
  requestVaultDir,
  reconnectVaultDir,
  type VaultDoc,
  type VaultDirHandle,
} from "@tether/storage";

export interface IngestReport {
  ingested: number;
  skipped: number;
  failed: number;
  firstId: string | null;
}

export interface UseVaultOptions {
  /** Auto-load files from a previously-granted FSAccess directory on mount.
   *  We only re-grant silently — never auto-prompt the picker (that has to
   *  come from a click handler). Default true. */
  autoReconnect?: boolean;
}

export function useVault(opts: UseVaultOptions = {}) {
  const autoReconnect = opts.autoReconnect ?? true;
  // Capture the live store reference. NOTE: re-resolved post-init below
  // because the WorkspacesManager singleton may not have init'd yet on the
  // first call, in which case getVaultStore() returns the legacy fallback —
  // a different VaultStore instance from the one the resolver eventually
  // routes to once `default` workspace is registered.
  let store = getVaultStore();

  const [docs, setDocs] = createSignal<VaultDoc[]>(store.signal());
  const [activeId, setActiveId] = createSignal<string | null>(null);
  const [ready, setReady] = createSignal(false);
  const [ingesting, setIngesting] = createSignal(false);
  const [toast, setToast] = createSignal<string | null>(null);
  const [vaultDir, setVaultDir] = createSignal<VaultDirHandle | null>(null);

  let toastTimer: ReturnType<typeof setTimeout> | null = null;
  function showToast(msg: string, ms = 3200) {
    setToast(msg);
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = setTimeout(() => setToast(null), ms);
  }

  const activeDoc = createMemo<VaultDoc | null>(() => {
    const id = activeId();
    if (!id) return null;
    return docs().find((d) => d.id === id) ?? null;
  });

  // The store callback fires on every CRUD; we re-snapshot the array. signal()
  // already returns a fresh sorted Array, so identity changes and Solid's
  // reactivity wakes up.
  let off = store.subscribe(() => setDocs(store.signal()));
  onCleanup(() => off());

  // Re-bind the subscription if the store identity changes after the
  // workspaces manager finishes init (legacy → per-workspace store swap).
  function rebindIfStoreSwapped(): void {
    const live = getVaultStore();
    if (live === store) return;
    off();
    store = live;
    off = store.subscribe(() => setDocs(store.signal()));
    setDocs([...store.signal()]); // fresh identity → wake downstream effects
  }

  onMount(async () => {
    // Ensure the workspaces resolver has finished migrating BEFORE we bind
    // to a store instance. Without this, fast-remounts after the manager
    // init lands on a different VaultStore instance from the one that owned
    // the seeded docs in the previous mount — the docs are still in IDB,
    // but our subscription is wired to the wrong (empty) instance until
    // re-fired below.
    try {
      await getWorkspacesManager().init();
    } catch {
      /* manager init failure is non-fatal — fall back to legacy singleton */
    }
    rebindIfStoreSwapped();

    await store.init();
    // Always emit a fresh array reference so Solid's referential-equality
    // signal comparator wakes up consumers (VaultOrbit3D's createEffect, etc.)
    // even when the previous emit happened to be the same Map snapshot.
    setDocs([...store.signal()]);
    setReady(true);

    // Re-emit on the next animation frame so the orbit engine — whose
    // launch() resolves async and which short-circuits its docs effect with
    // `if (!engine) return` until then — gets a fresh signal change AFTER
    // it's likely had a frame to mount. Belt-and-suspenders for the
    // page-change race ("only the brain core renders, refresh fixes it").
    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Two rAF ticks: first commits the current frame, second guarantees
          // the engine's onMount + async launch().then has had a chance to
          // bind. Cheap; runs once per mount.
          if (store.signal().length > 0) setDocs([...store.signal()]);
        });
      });
    }

    // Try the silent reconnect path. Never prompts.
    if (autoReconnect && fsAccessSupported()) {
      try {
        const d = await getVaultDir();
        if (d) setVaultDir(d);
      } catch {
        /* fine — caller can offer "pick folder" */
      }
    }
  });

  // ─── ingest ──────────────────────────────────────────────────────────────

  async function ingestFiles(files: File[] | FileList): Promise<IngestReport> {
    setIngesting(true);
    try {
      const batch = await parseFiles(files);
      let firstId: string | null = null;
      for (const result of batch.results) {
        const doc = await store.addDoc(result);
        if (doc && !firstId) firstId = doc.id;
      }
      const ingested = batch.results.length;
      const skipped = batch.skipped.length;
      const failed = batch.failed.length;
      return { ingested, skipped, failed, firstId };
    } finally {
      setIngesting(false);
    }
  }

  // ─── CRUD passthroughs ───────────────────────────────────────────────────

  async function createBlank(title?: string): Promise<VaultDoc> {
    const doc = await store.createBlankDoc(title);
    setActiveId(doc.id);
    return doc;
  }

  async function update(
    id: string,
    patch: Partial<Pick<VaultDoc, "title" | "content">>,
  ): Promise<void> {
    await store.updateDoc(id, patch);
  }

  async function remove(id: string): Promise<void> {
    await store.removeDoc(id);
    if (activeId() === id) setActiveId(null);
  }

  // ─── search ──────────────────────────────────────────────────────────────

  async function search(query: string, k = 10): Promise<Set<string>> {
    const q = query.trim();
    if (q.length === 0) return new Set();
    const hits = await store.searchDocs(q, k);
    return new Set(hits.map((h) => h.doc.id));
  }

  // ─── folder picker ───────────────────────────────────────────────────────

  /** Must be called from a user gesture. */
  async function pickFolder(): Promise<VaultDirHandle | null> {
    if (!fsAccessSupported()) {
      showToast("File System Access API not supported in this browser.");
      return null;
    }
    try {
      const handle = await requestVaultDir();
      setVaultDir(handle);
      // TODO(storage): once `@tether/storage` exposes a "scan and ingest a
      // FSAccess directory" helper, fan out from here. For v1 we just
      // remember the handle so writes can land back to disk later.
      showToast(`Vault folder set: ${handle.name}`);
      return handle;
    } catch (err) {
      // Aborted picker is not an error worth toasting.
      const msg = err instanceof Error ? err.message : String(err);
      if (!/AbortError|user.*cancel|aborted/i.test(msg)) {
        showToast(`Folder pick failed: ${msg}`);
      }
      return null;
    }
  }

  /** Re-prompt for permission on the previously-granted handle. */
  async function reconnectFolder(): Promise<VaultDirHandle | null> {
    const handle = await reconnectVaultDir();
    if (handle) setVaultDir(handle);
    return handle;
  }

  return {
    // state
    docs,
    activeId,
    activeDoc,
    ready,
    ingesting,
    toast,
    vaultDir,
    // selection
    setActiveId,
    // mutations
    ingestFiles,
    createBlank,
    update,
    remove,
    // search
    search,
    // folder
    pickFolder,
    reconnectFolder,
    // helpers re-exported for ergonomics
    isIngestable,
    showToast,
    // raw store, in case a caller needs the lower API. Getter so callers
    // (notably VaultExperience.tsx → seedDemo(vault.store)) always see the
    // post-swap instance after the workspaces manager init resolves.
    get store() {
      return store;
    },
  };
}

export type UseVault = ReturnType<typeof useVault>;
